import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
} from "react-bootstrap";
import "./admin-epaper.css";
import CommonTable from "../components/CommonTable";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";
const API_URL = `${API_BASE}/api/epaper`;

const AdminEPaper = () => {
  const [papers, setPapers] = useState([]);
  const [deletingDate, setDeletingDate] = useState(null);
  const [uploadDate, setUploadDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [uploadFile, setUploadFile] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchPapers();
  }, []);

  const fetchPapers = async () => {
    try {
      const res = await fetch(API_URL);
      const result = await res.json();
      const items = Array.isArray(result) ? result : result?.data || [];
      const deriveTitleFromFile = (item) => {
        const filePath = item.file || "";
        const parts = filePath.split("/").filter(Boolean);
        const filename = parts.length ? parts[parts.length - 1] : filePath;
        const name = filename.replace(/\.[^.]+$/, "");
        const pretty = name.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
        return pretty ? pretty.split(" ").map(s => s.charAt(0).toUpperCase()+s.slice(1)).join(" ") : filename || item.date || "Untitled";
      };

      setPapers((items || []).map((it) => ({ ...it, title: deriveTitleFromFile(it) })));
    } catch (err) {
      console.error(err);
      setError("Unable to load ePaper list. Please try again.");
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setError("");
    setMessage("");

    // Fallback: if uploadFile state is not set, try to read the file input from the form
    const selectedFile =
      uploadFile || e.currentTarget.querySelector('input[type=file]')?.files?.[0] || null;

    if (!selectedFile) {
      setError("Please choose a PDF file to upload.");
      return;
    }

    // Validate file type when possible
    if (selectedFile.type && selectedFile.type !== "application/pdf") {
      setError("Selected file is not a PDF. Please choose a PDF file.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("date", uploadDate);

      const res = await fetch(`${API_URL}/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 401) {
          // token invalid or expired
          localStorage.removeItem("token");
          setError("Session expired. Please login again.");
          navigate("/login");
          return;
        }
        throw new Error(data.message || "Upload failed");
      }

      setMessage("E-paper uploaded successfully.");
      setUploadFile(null);
      setUploadDate(new Date().toISOString().slice(0, 10));
      fetchPapers();
    } catch (err) {
      console.error(err);
      setError(err.message || "Upload failed. Please try again.");
    }
  };

  const handleDelete = async (date) => {
    if (!window.confirm(`Delete ePaper for ${date}?`)) return;

    setDeletingDate(date);
    setMessage("");
    setError("");

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Session expired. Please login again.");
      }

      const res = await fetch(`${API_URL}/${date}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem("token");
          setError("Session expired. Please login again.");
          navigate("/login");
          return;
        }
        throw new Error(data.message || "Delete failed");
      }

      setMessage("E-paper deleted successfully.");
      fetchPapers();
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to delete ePaper.");
    } finally {
      setDeletingDate(null);
    }
  };

  const columns = [
    { accessorKey: "date", header: "Date" },
    { accessorKey: "title", header: "Title" },
    {
      accessorKey: "file",
      header: "Preview / Download",
      Cell: ({ row }) => (
        <div>
          <a href={`${API_BASE}${row.original.file}`} target="_blank" rel="noreferrer">
            View
          </a>
          <span style={{ marginLeft: 8 }} />
          <a href={`${API_BASE}${row.original.file}`} download>
            Download
          </a>
        </div>
      ),
    },
    {
      accessorKey: "actions",
      header: "Action",
      Cell: ({ row }) => (
        <Button
          variant="danger"
          size="sm"
          disabled={deletingDate === row.original.date}
          onClick={() => handleDelete(row.original.date)}
        >
          {deletingDate === row.original.date ? "Deleting..." : "Delete"}
        </Button>
      ),
    },
  ];

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col md={8}>
          <h2>🗞 Admin ePaper Management</h2>
          <p className="text-muted">
            Upload new ePaper PDFs and manage existing issues from the admin
            panel.
          </p>
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={5}>
          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title>Upload New ePaper</Card.Title>
              <Card.Text>
                Choose a PDF file and set the issue date. The uploaded file will
                be available for users to view and download.
              </Card.Text>

              {message && <Alert variant="success">{message}</Alert>}
              {error && <Alert variant="danger">{error}</Alert>}

              <Form onSubmit={handleUpload}>
                <Form.Group className="mb-3" controlId="uploadDate">
                  <Form.Label>Issue Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={uploadDate}
                    onChange={(e) => setUploadDate(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-4" controlId="uploadFile">
                  <Form.Label>PDF File</Form.Label>
                  <Form.Control
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => {
                      const f = e.currentTarget?.files?.[0] || null;
                      setUploadFile(f);
                      setError("");
                      setMessage("");
                    }}
                    required
                  />
                  {uploadFile && (
                    <div className="small text-muted mt-2">Selected: {uploadFile.name}</div>
                  )}
                </Form.Group>

                <Button type="submit">
                  Upload ePaper
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title>Published ePapers</Card.Title>
              <Card.Text>
                View the current list of uploaded ePapers. Click the links to
                preview or download each issue.
              </Card.Text>

              {papers.length === 0 ? (
                <Alert variant="info">No ePapers uploaded yet.</Alert>
              ) : (
                <CommonTable columns={columns} data={papers} fileName="epapers" showSelection={false} />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminEPaper;
